<i18n>
{
    "uk": {
        "cp": "Креативна Практика",
        "learningMap": "Карта навчання",
        "allMaps": "Всі карти навчання",
        "progressBtn": "Прогрес",
        "closeTips": "Все зрозуміло",
        "mapProgress": "Прогрес по карті",
        "aboutModule": "Про курс",
        "progress": {
            "text": "Сукупний відсоток рахується, як відсоток всіх завершених курсів цієї карти.",
            "required": "Обов’язкові курси",
            "requiredText": "Зелене кільце показує, який відсоток обов’язкових курсів з цієї карти ви вже завершили.",
            "optional": "Додаткові курси",
            "optionalText": "Синє кільце показує, який відсоток необов’язкових (додаткових) курсів ви завершили. І хоч ми й називаємо їх необов’язковими, але часто навичкі та інструменти, пов’язані з ними, є важливими для працедавців."
        },
        "types": {
            "theory-practice": "Теорія та практика",
            "theory": "Теоретичний",
            "instrumental": "Інструментальний"
        },
        "aboutCourse": "Про курс",

        "help": {
            "title": "Ми створили для вас послідовну карту навчання за напрямом «{name}»",
            "text1": "Вона допоможе проходити навчання у найкомфортнішій послідовності!",
            "text2": "Використовуйте <strong>вертикальне та горизонтальне прокручування</strong> <span class=\"icon-ic-mice color-gray-1\"></span>, щоб пересуватись картою.",
            "text3": "Щоб дізнатись більше про курс <span class=\"icon-ic-info color-gray-1\"></span> — <strong>натискайте на картки курсів</strong>.",

            "solid": "Суцільні лінії пов’язують обов’язкові курси.",
            "dashed": "Пунктирні лінії означають опціональні курси.",
            "tick": "Галочка маркує пройдені курси."
        }
    },
    "en": {
        "cp": "Creative Practice",
        "learningMap": "Learning map",
        "allMaps": "All learning maps",
        "closeTips": "All clear",
        "progressBtn": "Progress",
        "mapProgress": "Map progress",
        "aboutModule": "About course",
        "progress": {
            "text": "Cumulative percentage is calculated as a percentage of all completed courses on this map.",
            "required": "Required courses",
            "requiredText": "The green ring displays what percentage of required courses on this map you have already completed.",
            "optional": "Optional courses",
            "optionalText": "The blue ring displays what percentage of optional (additional) courses you have completed. And even it calls optional, the skills and tools associated with them are important to employers."
        },
        "types": {
            "theory-practice": "Theory and practice",
            "theory": "Theoretical",
            "instrumental": "Instrumental"
        },
        "aboutCourse": "About course",
        "help": {
            "title": "We have created a sequential learning map for {name}",
            "text1": "It will help you to study in the most comfortable sequence!",
            "text2": "Use <strong>vertical and horizontal scrolling</strong> <span class=\"icon-ic-mice color-gray-1\"></span> to move around the map.",
            "solid": "Solid lines connect required courses.",
            "dashed": "Dashed lines means optional courses.",
            "tick": "A tick marks completed courses.",
            "text3": "To learn more about the course <span class=\"icon-ic-info color-gray-1\"></span> - <strong>click on the course cards</strong>."
        }
    }
}
</i18n>

<template>
    <section
        class="page"
        :class="{grabbing: isDragging}"
        @mousedown="onMouseDown"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
        @mouseenter="onMouseUp"
    >
        <NavigationBar>
            <template v-slot:left>
                <BackButton
                    :to="localePath('/learning/maps')"
                    class="layer-2"
                />
            </template>

            <template v-slot:right >
                <div class="d-flex">
                    <panel-button
                        class="btn-bg btn-round"
                        name="map-tips"
                    >
                        <span class="icon-ic-help icon-16" />
                    </panel-button>
                    <panel-button
                        class="btn-bg ml-1 ml-md-1h"
                        name="map-points"
                    >
                        <div class="d-none d-md-block">{{ $t('progressBtn') }}</div>
                        <div class="mini-points xs">
                            <dials
                                :type="'simple'"
                                :ring1="results.required"
                                :ring2="results.optional"
                                color1="green"
                                color2="blue"
                            />
                        </div>
                        <div class="fw-800">
                            {{results.total}}%
                        </div>
                    </panel-button>
                </div>
            </template>
        </NavigationBar>

        <div class="legend transition-up-1">
            <div class="page-container-wide pt-4 mb-6 ">
                <div class="row align-items-start">
                    <div class="col-lg-8">
                        <h1 class="fw-900 m-0 h3">{{ $t('learningMap') }} «{{ title[locale] }}»</h1>
                    </div>

                    <div class="d-none d-lg-flex col-4 mt-3 mt-lg-0 ">
                        <panel-button
                            class="btn-bg btn-lg rounded icon-ic-help-circle ml-lg-auto"
                            name="map-tips"
                        />
                        <panel-button
                            class="btn-bg ml-2 f-normal"
                            name="map-points"
                        >
                            <div class="">{{ $t('progressBtn') }}</div>
                            <div class="mini-points">
                                <dials
                                    :type="'simple'"
                                    :ring1="results.required"
                                    :ring2="results.optional"
                                    color1="green"
                                    color2="blue"
                                />
                            </div>
                            <div class="fw-800">
                                {{results.total}}%
                            </div>
                        </panel-button>
                    </div>
                </div>
            </div>
        </div>

        <div class="map d-flex transition-up-2" ref="map">
            <div
                v-for="(column, i) in mapColumns"
                :key="`column-${i}`"
                class="column"
            >
                <div
                    class="point"
                    v-for="(point, j) in column"
                    :key="`point-${i}-${j}`"
                    :style="{'z-index': 50 - j}"
                >
                    <div
                        v-if="point.type == 'group'"
                        class="group"
                    >
                        <button
                            v-for="(sub, k) in point.group"
                            :key="`point-${i}-${j}-${k}`"
                            class="site grouped btn-bg"
                            :disabled="!sub.course"
                            :class="{
                                passed: sub.passed,
                                busy: sub.course && loading == sub.course,
                                active: courseOpen && selectedCourse && selectedCourse.id == sub.course
                            }"
                            @click="showCourse(sub)"
                        >
                            <img :src="sub.icon" alt="" width="64" height="64" loading="lazy" class="site-icon">
                            <div class="site-title">
                                <div class="text">{{ sub.title[locale] }}</div>
                            </div>

                            <div v-if="sub.passed" class="tick icon-ic-tick-thin" />

                            <div v-else-if="sub.averageMark && sub.sepataredMark" class="progress">
                                <dials
                                    :type="'simple'"
                                    :ring1="sub.sepataredMark.theory"
                                    :ring2="sub.sepataredMark.practice"
                                />
                            </div>

                            <div
                                class="line join"
                                v-if="k != point.group.length - 1"
                                :class="{
                                    optional: point.group[k+1].optional,
                                    passed: point.group[k].passed
                                }"
                            />
                        </button>
                    </div>

                    <button
                        v-else
                        class="site btn-bg"
                        :disabled="!point.course"
                        :class="{
                            passed: point.passed,
                            busy: point.course && loading == point.course,
                            active: courseOpen && selectedCourse && selectedCourse.id == point.course
                        }"
                        @click="showCourse(point)"
                    >
                        <div class="site-icon"  v-if="!point.icon" />
                        <img :src="point.icon" alt="" v-else width="64" height="64" loading="lazy" class="site-icon">
                        <div class="site-title">
                            <div class="text">{{ point.title[locale] }}</div>
                        </div>

                        <div v-if="point.passed" class="tick icon-ic-tick-thin" />

                        <div v-else-if="point.averageMark && point.separatedMark" class="progress">
                            <dials
                                :type="'simple'"
                                :ring1="point.separatedMark.theory"
                                :ring2="point.separatedMark.practice"
                            />
                        </div>
                    </button>

                    <!-- Turn right -->
                    <div class="line right"
                        v-if="column[j+1]"
                        :class="{
                            first: j == 0,
                            optional: column[j+1].optional,
                            passed: column[j+1].type == 'single' ? column[j+1].passed : column[j+1].group[0].passed,
                        }"
                    />
                </div>

                <!-- Top line  -->
                <div
                    v-if="i !== mapColumns.length - 1"
                    class="line next"
                    :class="{optional: mapColumns[i+1][0].optional}"
                />

                <!-- Turn down -->
                <div
                    v-if="mapColumns[i+1] && mapColumns[i+1].length > 1"
                    class="line down"
                    :class="{ optional: mapColumns[i+1][1].optional }"
                />
            </div>
        </div>

        <client-only>
            <cases-panel
                modal-name="map-points"
                side="right"
                :def-title="$t('mapProgress')"
            >
                <div class="panel-course-points">

                    <div class="point-circles">
                        <dials
                            :ring1="results.required"
                            :ring2="results.optional"
                            :total="`${results.total}%`"
                            color1="green"
                            color2="blue"
                        />
                    </div>

                    <p> {{ $t('progress.text') }} </p>

                    <ul class="list-legend mt-0">
                        <li>
                            <div class="icon">
                                <div class="circle" style='--color: var(--color-cases-green)' />
                            </div>
                            <div class="text">
                                <div class="fw-800 mb-h">{{ $t('progress.required') }}</div>
                                <p class="f-small m-0">{{ $t('progress.requiredText') }}</p>
                            </div>
                        </li>

                        <li>
                            <div class="icon">
                                <div class="circle" style='--color: var(--color-cases-blue)' />
                            </div>
                            <div class="text">
                                <div class="fw-800 mb-h">{{ $t('progress.optional') }}</div>
                                <p class="f-small m-0">{{ $t('progress.optionalText') }}</p>
                            </div>
                        </li>
                    </ul>
                </div>
            </cases-panel>

            <cases-panel
                modal-name="map-tips"
                side="right"
                :classes="['headless panel-400 h-100']"
                @hide="closeHelp"
            >
                <div class="d-flex h-100 flex-column">
                    <div class="p-3">
                        <!-- <div class="help-video mb-3"> -->
                        <video loop autoplay muted playsinline
                            class="help-video mb-3"
                            width="1080"
                            height="720"
                        >
                            <source src="/video/maps/tip.webm" type="video/webm">
                            <source src="/video/maps/tip.mp4" type="video/mp4">
                            Your browser cannot play the provided video file.
                        </video>
                        <!-- </div> -->

                        <div class="f-semilarge fw-800 mb-2">
                            {{ $t('help.title', {name: title[locale]}) }}
                        </div>
                        <div>{{ $t('help.text1') }}</div>

                        <card :lower="true" class="p-2 mx-n2 my-4">
                            <div class="mb-2 d-flex align-items-center">
                                <div class="legend-mark">
                                    <div class="legend-solid" />
                                </div>
                                <div class="f-semismall">{{ $t('help.solid') }}</div>
                            </div>
                            <div class="mb-2 d-flex align-items-center">
                                <div class="legend-mark">
                                    <div class="legend-dashed" />
                                </div>
                                <div class="f-semismall">{{ $t('help.dashed') }}</div>
                            </div>
                            <div class="d-flex align-items-center">
                                <div class="legend-mark">
                                    <div class="legend-tick icon-ic-tick-thin" />
                                </div>
                                <div class="f-semismall">{{ $t('help.tick') }}</div>
                            </div>
                        </card>

                        <div class="mb-2" v-html="$t('help.text2')"/>
                        <div class="mb-2" v-html="$t('help.text3')"/>
                    </div>
                    <div class="panel-footer px-3 py-2 mt-auto d-flex elevate w-100 align-items-center">
                        <button
                            class="btn-primary w-100"
                            @click="$modalityStore.closeModal({type: 'drawer'})"
                        >
                            {{ $t('closeTips') }}
                        </button>
                    </div>
                </div>
            </cases-panel>

            <cases-panel
                modal-name="map-course"
                side="right"
                :classes="['h-100 headless panel-400']"
            >
                <div class="d-flex h-100 flex-column">
                    <div class="p-3">
                        <img
                            :src="selectedCourse.thumbnail"
                            alt=""
                            class="course-cover"
                            width=""
                        >

                        <div
                            v-if="selectedCourse.levels && selectedCourse.levels.length > 0"
                            class="chips mt-1"
                        >
                            <Chip v-if="selectedCourse.filters && selectedCourse.filters.type">
                                <template v-slot:text>
                                    {{ $t(`types.${selectedCourse.filters.type}`) }}
                                </template>
                            </Chip>

                            <template
                                v-for="(level, i) in selectedCourse.levels"
                            >
                                <Chip
                                    v-if="typeof level == 'string'"
                                    :key="`course-level-${i}`"
                                    :class="`colored ${levelColor(level)} fw-600`"
                                >
                                    <template v-slot:text>
                                        {{ $t(`course.levels.${level}`) }}
                                    </template>
                                </Chip>
                                <Chip
                                    v-else
                                    :key="`course-level-${i}`"
                                    :class="`colored ${level.color} fw-600`"
                                >
                                    <template v-slot:text>
                                        {{ level.text }}
                                    </template>
                                </Chip>
                            </template>
                        </div>

                        <div class="d-flex w-100">
                            <div class="fw-800 f-semilarge my-2">{{ $t('page.result.course') }} «{{ selectedCourse.name }}»</div>

                            <div
                                v-if="selectedCourse.rating && selectedCourse.rating.average"
                                class="d-flex align-items-center pl-1 ml-auto"
                            >
                                <span class="icon-ic-star color-cadmium-yellow mr-h"></span>
                                <span class="f-small color-gray-0">{{ selectedCourse.rating.average }}</span>
                            </div>
                        </div>




                        <div class="about" v-if="selectedCourse.about">
                            <div class="fw-600">{{ $t('aboutCourse') }}</div>
                            <div>{{ selectedCourse.about }}</div>
                        </div>

                        <div class="why mt-2" v-if="selectedCourse.explain">
                            <div class="fw-600" v-if="map.explainTitle">{{ map.explainTitle[locale] }}</div>
                            <div>{{ selectedCourse.explain }}</div>
                        </div>

                    </div>

                    <div class="panel-footer px-3 py-2 mt-auto d-flex elevate w-100 align-items-center">
                        <div class="d-none d-lg-block">{{ $t('page.course.mark') }}</div>
                        <div class="mini-points">
                            <dials
                                v-if="selectedCourse.separatedMark"
                                :type="'simple'"
                                :ring1="selectedCourse.separatedMark.theory"
                                :ring2="selectedCourse.separatedMark.practice"
                            />
                        </div>
                        <div>
                            <strong>{{ selectedCourse.averageMark }}</strong><span class="color-gray-2">/100</span>
                        </div>

                        <nuxt-link
                            v-if="!selectedCourse.awaiting || selectedCourse.hasLanding"
                            :to="localePath(`/learning/course/${selectedCourse.slug}/about`)"
                            class="btn-primary ml-auto"
                        >
                            {{ $auth.user ? $t('page.module.toCourse') : $t('aboutModule')}}
                        </nuxt-link>
                    </div>
                </div>
            </cases-panel>
        </client-only>
    </section>
</template>

<script>
import Dials        from '~/components/Dials.vue';
import PanelButton  from '~/components/panels/PanelButton.vue';

import Chip          from '~/components/chips/Chip.vue';
import Card          from '~/components/cards/Card.vue';
import BackButton    from '~/components/BackButton.vue';
import NavigationBar from '~/components/navigation/NavigationBar.vue';

import { useReportCardStore } from '~/stores/reportCard';

export default {
    scrollToTop: true,

    components: {
        Dials,
        CasesPanel: () => import('~/components/panels/CasesPanel.vue'),
        PanelButton,
        Chip,
        Card,
        BackButton,
        NavigationBar
    },

    head () {
        let title = `${this.$t('learningMap')} «${this.title[this.locale]}» | ${this.$t('cp')}`;
        let desc  = this.$t('help.title', {name: this.title[this.locale]});
        let url   = `${process.env.baseUrl}/learning/map/${this.map.slug}`;

        let meta = [
            { hid: 'title', name: "title", content: title },

            { hid: "og:url",   property: "og:url",   content: url },
            { hid: "og:title", property: "og:title", content: title },

            { hid: "twitter:url",   name: "twitter:url",   content: url },
            { hid: "twitter:title", name: "twitter:title", content: title },

            { name: 'viewport', content: 'minimum-scale=1, width=device-width, initial-scale=1, viewport-fit=cover' },
        ];

        if (desc) {
            meta.push({ hid: 'description',         name: 'description',         content: desc });
            meta.push({ hid: "og:description",      property: "og:description",  content: desc });
            meta.push({ hid: "twitter:description", name: "twitter:description", content: desc });
        }

        return {
            title: title,
            meta: meta,
            bodyAttrs: {
                class: 'gray-page canvas-mode'
            },
        }
    },

    data () {
        return {
            selectedCourse: {},
            loading: null,

            cursorPos: [0, 0],
            isDragging: false,

            title: {},
            mapColumns: []
        }
    },

    async asyncData({ $axios, route, error, app, $appStore }) {
        $appStore.$patch({activeApp: 'learning'});

        let data;
        let slug = route.params.slug;
        let queries = [
            $axios.$get(`/learningmap/find`, {
                params: {
                    slug,
                    // Show hidden learning maps
                    hidden: 1,
                },
            })
        ];

        const reportCardStore = useReportCardStore();
        if (!reportCardStore.hydrated) {
            queries.push(reportCardStore.hydrate())
        }

        await Promise.all(queries)
        .then(result => {
            [data] = result;
        })
        .catch((e) => {
            error({ statusCode: 404, message: 'Відео не знайдено' })
        });

        let maps = data.maps;
        if (!maps || !maps[0]) return error({ statusCode: 404, message: app.i18n.t('page.article.404') });


        let map = maps[0];
        let coursesCount = {
            optional: 0,
            required: 0,
            total: 0
        }
        let progressCounts = {
            optional: 0,
            required: 0,
            total: 0
        }

        const processCourse = (course) => {
            let card = reportCardStore.getCourseCard(course.course);
            course.passed = card && card.finished;

            if (course.optional) coursesCount.optional++;
            else coursesCount.required++;
            coursesCount.total++;

            if (course.passed) {
                if (course.optional) progressCounts.optional++;
                else progressCounts.required++;
                progressCounts.total++;
            }

            if (card && card.separatedMark) {
                course.separatedMark = card.separatedMark;
            }

            if (card && card.averageMark) {
                course.averageMark = card.averageMark;
            }


            return course;
        }


        map.map.forEach(column => {
            column.forEach(point => {
                if (point.type == 'single') {
                    point = processCourse(point);
                } else {
                    point.group.forEach(subPoint => {
                        subPoint = processCourse(subPoint);
                    })
                }
            })
        });

        let results = {
            optional: progressCounts.optional ? Math.round(progressCounts.optional / coursesCount.optional * 100) : 0,
            required: progressCounts.required ? Math.round(progressCounts.required / coursesCount.required * 100) : 0,
            total: progressCounts.total ? Math.round(progressCounts.total / coursesCount.total * 100) : 0
        }

        return {
            mapColumns: map.map,
            title: map.title,
            results: results,
            map: map
        }
    },

    computed: {
        reportCardStore () {
            return useReportCardStore();
        },

        courseOpen () {
            return this.$modalityStore.drawer == 'map-course';
        },

        locale () {
            return this.$i18n.locale;
        },

        helpClosed () {
            return this.$cookieStore.mapHelpClosed;
        }
    },

    mounted () {
        // document.body.classList.add("canvas-mode");

        window.addEventListener("mouseup", this.onMouseUp);

        if (!this.helpClosed) {
            this.$nextTick(() => {
                this.$modalityStore.openModal({type: 'drawer', name: 'map-tips'});
            })
        }
    },

    methods: {
        /** @param {MouseEvent} ev */
        onMouseDown(ev) {
            this.cursorPos = [ev.clientX, ev.clientY];
            this.isDragging = true;

            if (ev.target.closest('a, button')) return;

            window.addEventListener("mousemove", this.onMouseHold);
        },

        /** @param {MouseEvent} ev */
        onMouseUp(ev) {
            window.removeEventListener("mousemove", this.onMouseHold);
            this.isDragging = false;
        },

        /** @param {MouseEvent} ev */
        onMouseHold(ev) {
            ev.preventDefault();

            requestAnimationFrame(() => {
                const delta = [
                    ev.clientX - this.cursorPos[0],
                    ev.clientY - this.cursorPos[1],
                ];

                this.cursorPos = [ev.clientX, ev.clientY];

                if (!delta[0] && !delta[1]) return;


                window.scrollBy({
                    left: -delta[0],
                    top: -delta[1]
                });
            });
        },

        async showCourse (course) {
            if (this.loading) return true;

            this.loading = course.course;

            this.$axios.$get('/course/getInfo', {
                params: {id: course.course}
            })
            .then(res => {
                this.loading = null;

                let card = this.reportCardStore.getCourseCard(course.course);

                res.averageMark = card ? card.averageMark : 0;
                res.separatedMark = card && card.separatedMark || {};
                res.explain = course.explain ? course.explain[this.locale] : '';
                res.joined = !!card;


                if (!card && res.moduleTypes) {
                    res.moduleTypes.forEach(type => {
                        res.separatedMark[type] = 0;
                    });
                }

                this.selectedCourse = res;

                this.$modalityStore.openModal({type: 'drawer', name: 'map-course'});
            })
            .catch(err => {
                console.log(err);
                this.loading = null;
            })
        },

        closeHelp () {
            if (this.helpClosed) return;
            this.$cookieStore.setState("mapHelpClosed", true);
        },


        levelColor (level) {
                if (level == 'beginner') return 'success';
                if (level == 'intermediate') return 'yellow';
                if (level == 'advanced') return 'purple';
                return 'error';
            },
    },

    beforeDestroy () {
        // document.body.classList.remove("canvas-mode");
        window.removeEventListener("onMouseUp", this.onMouseUp);
    }
}
</script>

<style lang="postcss" scoped>
    .mini-points.xs {
        width: 16px;
        height: 16px;
    }

    .help-video {
        background-color: var(--color-cases-gray-5);
        width: 100%;
        height: auto;
        aspect-ratio: 3 / 2;
        border-radius: 8px;
        overflow: hidden;
        display: block;
    }

    .legend-mark {
        width: 32px;
        margin-right: 8px;
        flex-shrink: 0;
    }

    .legend-solid,
    .legend-dashed {
        height: 0;
        width: 100%;
        border-top: 2px solid var(--color-cases-lilac);
    }

    .legend-dashed {
        border-top: 2px dashed var(--color-cases-lilac);
    }

    .legend-tick {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: var(--color-cases-green);
        color: #fff;
        font-size: 10px;
    }

    .course-cover {
        border-radius: 8px;
        overflow: hidden;
    }

    .panel-course-points {
        padding: 0 24px 24px;
        display: flex;
        flex-direction: column;
        overflow: auto;

        .results-link {
            display: flex;
            flex-shrink: 0;
            justify-content: space-between;
            font-weight: 800;
        }

        .horizontal-delimeter {
            margin-bottom: 24px;
        }
    }

    .legend {
        position: sticky;
        width: 100vw;
        left: 0px;

        @media screen and (min-width: 768px) {
            width: calc(100vw - var(--menu-width));
            left: var(--menu-width);
        }
    }

    .page {
        position: relative;

        background: linear-gradient(90deg, var(--color-cases-gray-5) 21px, transparent 1%) center,
                    linear-gradient(var(--color-cases-gray-5) 21px, transparent 1%) center, var(--color-cases-gray-2);
        background-size: 22px 22px;

        cursor: grab;

        min-height: 100%;

        &.grabbing {
            cursor: grabbing
        }
    }

    .btn-flat {
        position: relative;

        color: var(--color-cases-gray-1);
        font-size: 22px;

        display: flex;
        align-items: center;
        justify-content: center;

        border-radius: 900px;
        background-color: transparent;
        transition: background-color .2s ease,
                    color .2s ease;

        cursor: pointer;
        outline: none;
        user-select: none;
        border: none;

        &:hover {
            background-color: var(--color-cases-gray-4);
        }

        &:active {
            background-color: var(--color-cases-blue-dim);
        }

        &.rounded {
            border-radius: 50%;
            width: 40px;
            height: 40px;
        }
    }


    .btn-flat.icon-ic-arrow-left {
        font-size: 18px;
    }

    .map {
        --space: 48px;
        --space-y: 80px;
        --card-height: 64px;
        --card-mid: 32px;
        --line-width: 2px;
        --radius: 32px;

        --text-margin: 8px;
        --passed-outline: 3px;

        --mid: calc(var(--card-mid) - var(--line-width) /2);

        @media screen and (min-width: 576px) {
            --space: 64px;
            --space-y: 64px;
            --card-height: 80px;
            --card-mid: 40px;
            --passed-outline: 2px;
        }

        padding: 0 16px;

        @media screen and (min-width: 768px) {
            padding: 0 32px;
        }

        @media  screen and (min-width: 1200px) {
            padding: 0 calc((100vw - var(--menu-width) - 1032px)/2);
        }

        @media  screen and (min-width: 1200px) {
            padding: 0 max(calc((100vw - var(--menu-width) - 1392px)/2), 32px);
        }



        .line {
            position: absolute;
            border-color: var(--color-cases-lilac);
            border-style: solid;
            border-width: 0;
            pointer-events: none;

            &.optional {
                border-style: dashed;
            }
        }

        .next {
            z-index: 12;
            right: calc(var(--space) * -1);
            top: var(--mid);

            height: 0;
            width: calc(var(--space) * 2);
            border-top-width: var(--line-width);
        }

        .join {
            left: calc(50% - var(--line-width) / 2);
            bottom: calc(var(--space-y) * -1 - var(--text-margin));

            height: calc(var(--space-y) - var(--text-margin));
            width: 0;

            border-right-width: var(--line-width);

            @media screen and (min-width: 576px) {
                bottom: calc(var(--space-y) / -2);
                height: calc(var(--space-y) / 2);
                width: 0;

                &.passed {
                    height: calc(var(--space-y) / 2 - var(--passed-outline));
                }
            }
        }

        .down {
            top: var(--mid);
            right: calc(var(--line-width) / -2);

            width: var(--radius);
            height: calc(var(--radius) + var(--line-width) * 2);

            border-top-width: var(--line-width);
            border-right-width: var(--line-width);
            border-radius: 0 var(--radius) 0px 0;
        }

        .right {
            --width: calc(var(--space) + var(--line-width) / 2);

            top: 0;
            left: calc((var(--space) * -1) - (var(--line-width) / 2));

            width: var(--width);
            height: calc(100% + var(--card-mid));

            border-bottom-width: var(--line-width);
            border-left-width: var(--line-width);
            border-radius: var(--line-width) 0 0 var(--radius);

            &.first {
                top: calc(var(--card-mid) + var(--radius) - var(--line-width));
                height: calc(100% - var(--radius) + var(--line-width));
            }

            &.passed {
                width: calc(var(--width) - var(--passed-outline))
            }
        }
    }



    .column {
        position: relative;
        padding: 0 var(--space);

        &:last-child  { padding-right: 18px; }
        &:first-child { padding-left: 18px; }

        @media screen and (min-width: 576px) {
            &:last-child  { padding-right: 0; }
            &:first-child { padding-left: 0; }
        }
    }

    .point {
        position: relative;

        &:not(:last-child) {
            padding-bottom: var(--space-y);
        }
    }



    .grouped + .grouped {
        margin-top: var(--space-y);

        @media screen and (min-width: 576px) {
            margin-top: calc(var(--space-y) / 2);
        }
    }

    .site {
        position: relative;
        z-index: 50;

        display: flex;
        align-items: center;
        justify-content: flex-start;

        height: var(--card-height);
        width: 64px;
        padding: 0;

        border-radius: 12px;
        background-color: var(--color-control);
        font-weight: 700;
        text-align: left;

        &[disabled] {
            background-color: var(--color-control) !important;
        }

        &.passed {
            box-shadow: 0px 1px 1px var(--color-cases-secondary-button-shadow-2),
                0px 1px 4px var(--color-cases-secondary-button-shadow-1),
                0px 0px 1px var(--color-cases-shadow-border),
                0 0 0 var(--passed-outline) var(--color-cases-green);

            &:hover {
                box-shadow: 0px 0px 3px rgba(14, 16, 20, 0.08),
                            0px 4px 8px var(--color-cases-secondary-button-shadow-1),
                            0px 0px 1px var(--color-cases-shadow-border),
                            0 0 0 var(--passed-outline) var(--color-cases-green);
            }

            &.exact-active,
            &.active {
                box-shadow: 0px 1px 1px var(--color-cases-secondary-button-shadow-2),
                            0px 1px 4px var(--color-cases-secondary-button-shadow-1),
                            0px 0px 0px var(--color-cases-shadow-border),
                            0 0 0 4px rgba(27, 139, 224, 0.32);
            }

            &:active {
                box-shadow: 0px 0px 2px rgba(9, 10, 13, 0.24),
                            0px 0px 0px var(--color-cases-secondary-button-shadow-1),
                            0px 0px 1px var(--color-cases-shadow-border),
                            0 0 0 var(--passed-outline) var(--color-cases-green);
            }
        }


        @media screen and (min-width: 576px) {
            width: 245px;
            padding: 8px;
            border-radius: 16px;
        }


        .tick {
            position: absolute;
            top: -12px;
            right: -12px;

            display: flex;
            justify-content: center;
            align-items: center;

            width: 24px;
            height: 24px;

            background-color: var(--color-cases-green);
            color: #fff;
            font-size: 8px;

            border-radius: 50%;

            box-shadow: 0px 1px 1px var(--color-cases-secondary-button-shadow-2),
                        0px 1px 4px var(--color-cases-secondary-button-shadow-1);

            @media screen and (min-width: 576px) {
                width: 32px;
                height: 32px;

                top: -16px;
                right: -16px;

                font-size: 10px;
            }
        }

        .progress {
            position: absolute;
            top: -12px;
            right: -12px;

            width: 25px;
            height: 25px;

            border-radius: 50%;
            border: 1px solid var(--color-layer-2);
            background-color: var(--color-layer-2);
            box-shadow: 0px 1px 1px var(--color-cases-secondary-button-shadow-2),
                        0px 1px 4px var(--color-cases-secondary-button-shadow-1);
        }
    }

    .site-icon {
        border-radius: 8px;
        overflow: hidden;
        flex-shrink: 0;
        border-radius: 12px;

        width: 64px;
        height: 64px;
        background-color: var(--color-cases-gray-1);

        @media screen and (min-width: 576px) {
            margin-right: 8px;
        }
    }

    @media screen and (max-width: 575px) {
        .site-title {
            position: absolute;
            bottom: calc(var(--text-margin) * -1);
            left: -16px;
            z-index: 20;

            width: 98px;
            height: 48px;

            transform: translateY(100%);

            font-size: 12px;
            font-weight: 600;
            text-align: center;

            overflow: hidden;

            .text {
                background-color: var(--color-cases-gray-5);
                padding-bottom: 4px;
            }
        }
    }
</style>
